/**
 * Averaging agreements (domain-model-transition P7a, worklist W5 — register B4).
 *
 * WHY THIS BLOCKS THE NEXT PHASE. `scheduled_daily_hours` displaces the statutory threshold
 * (P6's D35), so an UNRECORDED agreement classifies a scheduled 10-hour day as 8 + 2. That is not
 * a gap in the data — it is silently wrong pay for every day of a rotation. No deployment enables
 * 7b's classification without one, which is why the form ships here rather than in 7b: a surface
 * that gates another phase should not be built inside it.
 *
 * PLACEMENT IS A SECURITY DECISION, NOT TASTE. This cannot sit on the project detail beside the
 * Sites tab, which any matter member can reach: P6's review moved work-agreement READS behind the
 * permish {1,2} wall because `?scope_user_id=<colleague>` would otherwise answer whether a named
 * individual holds an averaging arrangement.
 *
 * THE GATE IS {1}, NOT {1,2}, AND THE DIFFERENCE IS THE BUG THIS PHASE INHERITED AN OBLIGATION TO
 * AVOID. The server gates the two GETs on {1,2} and every mutation on {1}. Gating the controls on
 * the read wall would hand a permish-2 admin a form that 403s on submit — exactly the mismatch
 * `ProjectSitesTab`'s header comment documents. Read the list at {1,2}; offer the form at {1}.
 *
 * CLOSE, NEVER DELETE. Once an agreement has governed real time, deleting it leaves those hours
 * with no record of why they averaged — so the action writes `effective_to`, and the server 409s
 * a delete whose window contains recorded time.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useCurrentEmployee from '@frontend/AppSettings/hooks/useCurrentEmployee';
import SiteService from '@frontend/AppSettings/services/SiteService';
import WorkAgreementService, {
  type CreateWorkAgreementBody,
} from '@frontend/AppSettings/services/WorkAgreementService';

// LOCAL date, not UTC (review R8): toISOString is UTC, so every evening west of Greenwich it
// named TOMORROW — and the close path submits this with no user-visible field, so an agreement
// closed at 6:30pm MDT would have closed a day late, leaving one extra governed day.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function WorkAgreementsPanel() {
  const queryClient = useQueryClient();
  const currentEmployee = useCurrentEmployee();
  const rawPermish = currentEmployee?.permish_id ?? null;
  const permish = typeof rawPermish === 'string' ? parseInt(rawPermish, 10) : rawPermish;
  // The SERVER's write predicate, not the nearest client-side approximation.
  const mayManage = permish === 1;

  const [scopeType, setScopeType] = useState<'matter' | 'user'>('matter');
  const [scopeValue, setScopeValue] = useState('');
  const [jurisdictionId, setJurisdictionId] = useState('');
  const [weeks, setWeeks] = useState('');
  const [scheduledHours, setScheduledHours] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayISO);
  const [error, setError] = useState<string | null>(null);

  const agreements = useQuery({
    queryKey: ['WorkAgreements'],
    queryFn: () => WorkAgreementService.list(),
  });
  const jurisdictions = useQuery({
    queryKey: ['Jurisdictions'],
    queryFn: () => SiteService.getJurisdictions(),
  });

  const jurisdictionName = useMemo(() => {
    const byId = new Map((jurisdictions.data ?? []).map((j) => [j.id, j.name ?? j.code]));
    return (id: string) => byId.get(id) ?? id;
  }, [jurisdictions.data]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['WorkAgreements'] });

  const add = useMutation({
    mutationFn: () => {
      const body: CreateWorkAgreementBody = {
        scope_type: scopeType,
        ...(scopeType === 'matter'
          ? { scope_matterid: scopeValue.trim() }
          : { scope_user_id: scopeValue.trim() }),
        jurisdiction_id: jurisdictionId,
        agreement_type: 'averaging',
        effective_from: effectiveFrom,
        ...(weeks !== '' ? { averaging_period_weeks: Number(weeks) } : {}),
        // Omitted when blank, NEVER sent as 0: "no schedule recorded" and "scheduled zero" are
        // different facts, and D27 ships the column with no default for exactly that reason.
        ...(scheduledHours !== '' ? { scheduled_daily_hours: Number(scheduledHours) } : {}),
      };
      return WorkAgreementService.create(body);
    },
    onSuccess: () => {
      setScopeValue(''); setWeeks(''); setScheduledHours(''); setError(null);
      void refresh();
    },
    onError: (err: unknown) => setError((err as Error)?.message || 'Could not add the agreement.'),
  });

  const close = useMutation({
    mutationFn: (vars: { id: string; on: string }) =>
      WorkAgreementService.close(vars.id, vars.on),
    onSuccess: () => { setError(null); void refresh(); },
    onError: (err: unknown) => setError((err as Error)?.message || 'Could not close the agreement.'),
  });

  const rows = agreements.data ?? [];
  // The server's schema requires averaging_period_weeks (1–52, no default) — a submit without
  // it is a guaranteed 400, so the button must not offer one (review R8: the panel's own gate
  // doctrine, applied to the field it missed).
  const weeksNum = Number(weeks);
  const canSubmit = scopeValue.trim().length > 0 && jurisdictionId.length > 0
    && effectiveFrom.length > 0
    && weeks !== '' && Number.isInteger(weeksNum) && weeksNum >= 1 && weeksNum <= 52
    && !add.isPending;

  return (
    <div className="bid-admin-defaults">
      <p className="bid-admin-hint">
        An averaging agreement is the legal instrument that lets a rotation&apos;s hours average
        before overtime is computed. Overtime cannot be enabled for a project without one where
        one applies — an unrecorded agreement classifies a scheduled 10-hour day as 8 + 2.
      </p>

      {agreements.isLoading && <p className="bid-admin-empty">Loading…</p>}
      {agreements.isError && rows.length === 0 && (
        <p className="bid-admin-empty">
          The agreements could not be loaded. This view needs administrator access.
        </p>
      )}
      {agreements.isSuccess && rows.length === 0 && (
        <p className="bid-admin-empty">No agreements recorded yet.</p>
      )}

      {rows.length > 0 && (
        <table>
          <thead>
            <tr>
              <th scope="col">Scope</th>
              <th scope="col">Jurisdiction</th>
              <th scope="col">Averaging</th>
              <th scope="col">Scheduled day</th>
              <th scope="col">From</th>
              <th scope="col">To</th>
              {mayManage && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((agreement) => (
              <tr key={agreement.id}>
                <th scope="row">
                  {agreement.scope_type === 'matter'
                    ? `Project ${agreement.scope_matterid}`
                    : 'One person'}
                </th>
                <td>{jurisdictionName(agreement.jurisdiction_id)}</td>
                <td>
                  {agreement.averaging_period_weeks
                    ? `${agreement.averaging_period_weeks} wk`
                    : '—'}
                </td>
                {/* Blank means NOT RECORDED, which is a different fact from eight. */}
                <td>{agreement.scheduled_daily_hours ?? '—'}</td>
                <td>{agreement.effective_from}</td>
                <td>{agreement.effective_to ?? 'open'}</td>
                {mayManage && (
                  <td>
                    {!agreement.effective_to && (
                      <button
                        type="button"
                        className="bids-walk-btn"
                        disabled={close.isPending && close.variables?.id === agreement.id}
                        onClick={() => close.mutate({ id: agreement.id, on: todayISO() })}
                        title="Close this agreement with today's date — it is never deleted, so the
                               hours it governed keep their reason"
                      >
                        Close
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {mayManage ? (
        <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) add.mutate(); }}>
          {/* Matter scope FIRST: the tenant's rotations vary by JOB (10-on/4-off at two weeks,
              20-on/8-off at four, shop staff none), so matter is the common case and a person is
              the exception. */}
          <label>
            Applies to
            <select
              value={scopeType}
              onChange={(e) => {
                setScopeType(e.target.value as 'matter' | 'user');
                setScopeValue('');
              }}
            >
              <option value="matter">A project</option>
              <option value="user">One person</option>
            </select>
          </label>
          <label>
            {scopeType === 'matter' ? 'Project id' : 'Employee id'}
            <input value={scopeValue} onChange={(e) => setScopeValue(e.target.value)} />
          </label>
          <label>
            Jurisdiction
            <select
              value={jurisdictionId}
              onChange={(e) => setJurisdictionId(e.target.value)}
            >
              <option value="">(choose)</option>
              {(jurisdictions.data ?? []).map((j) => (
                <option key={j.id} value={j.id}>{j.name ?? j.code}</option>
              ))}
            </select>
          </label>
          <label>
            Averaging period (weeks)
            <input
              type="number" min="1" max="52" value={weeks}
              onChange={(e) => setWeeks(e.target.value)}
            />
          </label>
          <label>
            Scheduled daily hours
            <input
              type="number" min="0" max="24" step="0.5" value={scheduledHours}
              onChange={(e) => setScheduledHours(e.target.value)}
              title="Leave blank if the rotation records no scheduled day — blank and zero are
                     different facts, and blank is the honest one"
            />
          </label>
          <label>
            Effective from
            <input
              type="date" value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </label>
          <button type="submit" className="bids-walk-btn" disabled={!canSubmit}>
            {add.isPending ? 'Recording…' : 'Record agreement'}
          </button>
        </form>
      ) : (
        <p className="bid-admin-hint">
          Recording an agreement needs system-administrator access.
        </p>
      )}

      {error && <p className="bid-admin-error" role="alert">{error}</p>}
    </div>
  );
}
